import type { ReactNode } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Navigate } from 'react-router-dom';
import { readPersonalPage } from '../lib/storage';

/**
 * Gate that ensures a Clerk session is active before rendering children.
 *
 * Routing decisions:
 *   Clerk still loading       → render nothing (briefly — Clerk loads fast)
 *   Not signed in + has page  → /sign-up   (mid-onboarding, just redeemed)
 *   Not signed in + no page   → /          (start from home)
 *   Signed in                 → render children
 *
 * Hydration of localStorage `personalPage` from the API (for new-device
 * sign-ins) happens in /onboarding/complete, NOT here — this gate is the
 * cheap synchronous check. By the time RequireAuth lets a user through to
 * /me or /settings, /onboarding/complete has already populated
 * personalPage on this device, so consumers can rely on its presence.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn } = useAuth();
  if (!isLoaded) return null;
  if (!isSignedIn) {
    const hasMidOnboarding = readPersonalPage() !== null;
    return <Navigate to={hasMidOnboarding ? '/sign-up' : '/'} replace />;
  }
  return <>{children}</>;
}
